import React, { FC, useEffect, useRef, useState } from 'react';

import { Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Button         from "@material-ui/core/Button";
import LinearProgress from '@material-ui/core/LinearProgress';
import Snackbar       from '@material-ui/core/Snackbar';
import IconButton     from '@material-ui/core/IconButton';
import Fab            from '@material-ui/core/Fab';

import CloseIcon     from '@material-ui/icons/Close';
import WallPaperIcon from '@material-ui/icons/Wallpaper';


import { FormattedMessage, useIntl } from 'react-intl';

import { CustomerTemplate, RenderTemplateCallback } from "../types";
import { FieldData, SignatureField }                from "../hooks/customer";
import { selectText }                               from "../utils";
import { TemplateUtils }                            from "../template-utils";


const useStyles = makeStyles(theme => ({
    buttonBar: {
        display: 'flex',
        alignItems: 'flex-end',
        flexDirection: 'column',
        flexGrow: 1,
        margin: theme.spacing(1)
    },
    fab: {
        position: 'fixed',
        top: 60 + theme.spacing(2),
        right: theme.spacing(2),
        display: 'none',
        [theme.breakpoints.down('xs')]: {
            display: 'flex'
        }
    },
    canvas: {
        border: 'solid rgb(0,0,0,0.3) 2px'
    },
    errorOverlay: {
        position: 'relative'
    },
    errorObject: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0, 0.75)',
        backdropFilter: 'blur(15px)',
        WebkitBackdropFilter: 'blur(15px)',
        MozBackdropFilter: 'blur(15px)'
    },
    errorContent: {
        margin: theme.spacing(3),
        textAlign: 'center'
    }
}));

export interface TemplateRendererProps {
    template: CustomerTemplate;
    data: FieldData;
    signatures: SignatureField[];
    renderTemplate: RenderTemplateCallback;
    templateLanguage: string;
    mailtoUrl?: string;
}

export const TemplateRenderer: FC<TemplateRendererProps> = ({ renderTemplate, template, data, signatures, templateLanguage, mailtoUrl}) => {

    const classes = useStyles();

    const intl = useIntl();

    const containerRef = useRef<HTMLDivElement>(null);
    const utilsRef = useRef<TemplateUtils>(new TemplateUtils());
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isLoading, setLoading] = useState<boolean>(true);
    const [isRendering, setRendering] = useState<boolean>(false);
    const renderPromiseRef = useRef<Promise<void>>();
    const [hasError, setError] = useState<boolean>(false);

    const [open, setOpen] = useState<boolean>(false);

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        handleRenderTemplate().then();
    }, [data, template, signatures]);

    useEffect(() => {
        if ((document as any).fonts) {
            if((document as any).fonts.ready) {
                (document as any).fonts.ready.then(() => {
                    setLoading(false);
                    handleRenderTemplate().then();
                });
            } else {
                (document as any).fonts.onloadingdone = () => {
                    setLoading(false);
                    handleRenderTemplate().then();
                };
            }

        } else {
            console.log("Document fonts not supported, using simple timeout");
            setTimeout(() => {
                console.log("Rendering")
                setLoading(false);
                handleRenderTemplate().then();
            }, 2000);
        }

        setTimeout(() => {
            handleRenderTemplate().then();
        }, 3000);

    }, []);

    useEffect(() => {
        if (canvasRef.current) {
            canvasRef.current.style.display = (isLoading) ? 'none' : 'block';
        }
    }, [isLoading]);

    const handleScrollToPreview = () => {
       containerRef.current?.scrollIntoView({
           behavior: 'smooth'
       })
    }

    const handleRenderTemplate = async () => {
        if (renderPromiseRef.current) {
            console.log("Render in Progress, waiting....");
            await renderPromiseRef.current;
        }
        if (canvasRef.current && data && template) {
            if (Object.keys(data).length === 0) {
                console.log("Skip rendering, empty data");
                setError(false);
                return;
            } else if (Object.values(data).some(f => !f.valid)) {
                setError(true);
                return;
            } else {
                setError(false);
            }

            const canvas = canvasRef.current;

            const context = canvas.getContext('2d');

            if (context) {
                console.log("Starting rendering...");
                setRendering(true);
                context.save();
                renderPromiseRef.current = renderTemplate(context, utilsRef.current, template, data, signatures, templateLanguage);

                await renderPromiseRef;
                console.log("Rendering done...");
                context.restore();
                setRendering(false);
                renderPromiseRef.current = undefined;
            }
        }
    }

    const handleOpenMail = () => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;

            const img = document.createElement('img');
            img.src = canvas.toDataURL()

            const div = document.createElement('div');
            (div as any).contentEditable = true;
            div.appendChild(img);
            document.body.appendChild(div);

            selectText(div);
            document.execCommand('Copy');
            document.body.removeChild(div);

            setOpen(true);

            // Open custom mailto URL if configured
            if (mailtoUrl) {
                window.location.href = mailtoUrl;
            }
        }

    }

    const width = template.width * template.scale;
    const height = template.height * template.scale;

    return <div ref={containerRef}>
        { (isLoading || isRendering) && <LinearProgress/> }
        <div className={ classes.buttonBar }>
            <Button onClick={ handleOpenMail } variant={ 'contained' } color={ 'secondary' } disabled={ hasError }>
                <FormattedMessage id={ 'preview.copy-image' }/>
            </Button>
        </div>
        <div className={ classes.errorOverlay }>
            {
                hasError && <div className={ classes.errorObject }>
                    <Typography className={ classes.errorContent } variant={ 'h6' } color={ 'error' }>
                        <FormattedMessage id={ 'errors.data' }/>
                    </Typography>
                </div>
            }
            <canvas
                className={ classes.canvas }
                ref={ canvasRef }
                width={ width }
                height={ height }
                style={ { width: '100%' }
                }/>
        </div>
        <Snackbar
            anchorOrigin={ {
                vertical: 'top',
                horizontal: 'center',
            } }
            open={ open }
            autoHideDuration={ 6000 }
            title={ '' }
            message={ intl.formatMessage({ id: 'preview.clipboard' }) }
            action={
                <React.Fragment>
                    <IconButton size="small" color="inherit" onClick={ handleClose }>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </React.Fragment>
            }
        />
        <Fab
            className={ classes.fab }
            color={'secondary'}
            onClick={handleScrollToPreview}
        >
            <WallPaperIcon/>
        </Fab>
    </div>
}